import React, { CSSProperties, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ResourceAutoComplete from '../../../component/resourceautocomplete';

// @ts-ignore
import { ReactComponent as MoreIcon } from '../../../assets/images/menu-trigger.svg';
// @ts-ignore
import { ReactComponent as CopyIcon } from '../../../assets/images/menu-duplicate.svg';
// @ts-ignore
import { ReactComponent as TrashIcon } from '../../../assets/images/stock-levels/button-remove-line-red-bg.svg';
// @ts-ignore
import { ReactComponent as EditIcon } from '../../../assets/images/edit-icon.svg';
// @ts-ignore
import { ReactComponent as RefreshIcon } from '../../../assets/images/refresh_icon.svg';
// @ts-ignore
import { ReactComponent as MergeRightIcon } from '../../../assets/images/merge-right-icon.svg';
// @ts-ignore
import { ReactComponent as MergeLeftIcon } from '../../../assets/images/merge-left-icon.svg';
// @ts-ignore
import { ReactComponent as MergeMultipleIcon } from '../../../assets/images/merge_multiple_icon.svg';
// @ts-ignore
import { ReactComponent as UngroupIcon } from '../../../assets/images/ungroup_cell_icon.svg';
// @ts-ignore
import { ReactComponent as SaveIcon } from '../../../assets/images/save-icon-indigo.svg';
// @ts-ignore
import { ReactComponent as CancelRedIcon } from '../../../assets/images/planograms/cancel-red.svg';
// @ts-ignore
import { ReactComponent as ProductImageSkeletonIcon } from '../../../assets/images/planograms/product-image-skeleton.svg';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { ProductService } from '../../../service/ProductService';
import { PlanogramEditMode } from '../enums/PlanogramEditMode';
import { parsePriceFieldInput } from '../utils/PlanogramPriceUtils';

// @ts-ignore
import './PlanogramCell.scss';

type PlanogramEditModeValue = 'add_product' | 'initialization';

type ProductOption = {
    id?: string | number;
    value?: string | number;
    externalId?: string | number;
    systemId?: string | number;
    label?: string;
    name?: string;
    ean?: string | number;
    price?: unknown;
    minQuantity?: unknown;
    maxQuantity?: unknown;
    [key: string]: unknown;
};

type PlanogramCellData = {
    id?: string;
    type?: string;
    product?: {
        id?: string | number | null;
        name?: string;
        label?: string;
        ean?: string | number | null;
        categoryName?: string | null;
        categoryColor?: string | null;
        currency?: string | null;
        price?: unknown;
        minQuantity?: unknown;
        maxQuantity?: unknown;
        [key: string]: unknown;
    } | null;
    price?: unknown;
    minQuantity?: unknown;
    maxQuantity?: unknown;
    mdbId?: string | number | null;
    evaDtsId?: string | number | null;
    position?: unknown;
    merged?: boolean;
    mergeData?: {
        colspan?: number;
        isMerged?: boolean;
        [key: string]: unknown;
    } | null;
    productName?: string;
    [key: string]: unknown;
};

type AssignIdForm = {
    rowIndex?: number;
    colIndex?: number;
    mdbId?: string;
    evaDtsId?: string;
    [key: string]: unknown;
};

type AssignIdErrors = {
    mdb?: boolean;
    eva?: boolean;
    [key: string]: unknown;
};

type ProductSearchEvent = {
    query: string;
};

const PlanogramCellProductPhoto = ({
    ean,
    alt,
}: {
    ean?: string | number | null;
    alt: string;
}) => {
    const [hasFailedToLoad, setHasFailedToLoad] = useState(false);
    const photoUrl = ean ? ProductService.getPhotoUrl({ ean }) : null;

    if (!photoUrl || hasFailedToLoad) {
        return <ProductImageSkeletonIcon className="product-preview-image-placeholder" />;
    }

    return (
        <img
            className="product-preview-image"
            src={photoUrl}
            alt={alt}
            onError={() => setHasFailedToLoad(true)}
        />
    );
};

interface PlanogramCellProps<TOption extends ProductOption = ProductOption> {
    cell: PlanogramCellData;
    rowIndex: number;
    colIndex: number;

    onEditProduct?: (rowIndex: number, colIndex: number) => void;
    onMergeRight?: (rowIndex: number, colIndex: number) => void;
    onMergeLeft?: (rowIndex: number, colIndex: number) => void;
    onMergeMultiple?: (rowIndex: number, colIndex: number) => void;
    onClearCell?: (rowIndex: number, colIndex: number) => void;
    onAddProduct?: (
        rowIndex: number,
        colIndex: number,
        cell: PlanogramCellData,
        productOption: TOption,
    ) => void;
    onUpdateCellField?: (
        rowIndex: number,
        colIndex: number,
        field: string,
        value: string,
    ) => void;

    /**
     * Clicking a passive (view-only) assigned slot badge during
     * INITIALIZATION-mode preview — see renderCell below — calls this
     * to re-enter manual initialization focused on that cell
     * (PlanogramDevice's handleAssignInitialization sets
     * manualInitializationActive=true and activeInitializationCell).
     */
    onAssignInitialization?: (
        rowIndex: number,
        colIndex: number,
        cell: PlanogramCellData,
    ) => void;
    onEditAssign?: (
        rowIndex: number,
        colIndex: number,
        cell: PlanogramCellData,
    ) => void;
    onTestCell?: (
        rowIndex: number,
        colIndex: number,
        cell: PlanogramCellData,
    ) => void;

    /**
     * Clicking a passive assigned slot badge during ADD_PRODUCT-mode
     * PREVIEW (see renderCell / renderAssignedSlotBadge below) calls
     * this instead of onAssignInitialization — PlanogramDevice's
     * handleEnterAddProductEdit sets addProductEditActive=true, which
     * swaps the whole grid over to the full NAME/PRICE/MIN/MAX editor
     * cards (renderEditableCell), mirroring how clicking a badge during
     * INITIALIZATION preview re-enters manual MDB assignment.
     */
    onEnterAddProductEdit?: () => void;

    isSelectionMode?: boolean;
    isSelected?: boolean;
    onSelectionToggle?: (rowIndex: number, colIndex: number) => void;

    totalColumns: number;
    onUnmerge?: (rowIndex: number, colIndex: number) => void;
    isRowFullyMerged?: boolean;

    mode?: PlanogramEditModeValue;
    onlyMerge?: boolean;
    isReadOnlyMode?: boolean;

    /**
     * Legacy single-active-cell popup state. No longer used to drive
     * rendering (see renderInitializationSlotCard / onChangeMdbId below,
     * which replaced the old "click a cell to open a popup form" flow
     * with Figma's always-visible-per-slot inputs, matching how
     * PlanogramTabular already worked). Kept in the prop signature only
     * so PlanogramVisual — which still threads these through from
     * PlanogramDevice for other reasons — doesn't need every call site
     * touched.
     */
    activeInitializationCell?: {
        rowIndex?: number;
        colIndex?: number;
        [key: string]: unknown;
    } | null;
    assignIdForm?: AssignIdForm;
    assignIdErrors?: AssignIdErrors;
    onSaveAssignId?: () => void;
    onAssignIdFormChange?: (form: AssignIdForm) => void;
    onCancelAssignId?: () => void;

    supportEveDts?: boolean;
    manualInitializationActive?: boolean;

    /**
     * Direct write-through handlers for the always-visible MDB/EVA-DTS
     * inputs rendered by renderInitializationSlotCard — mirrors
     * PlanogramTabular's identical onChangeCellMdb/onChangeCellEva
     * props (same handlers on the PlanogramDevice side:
     * handleChangeCellMdb/handleChangeCellEva already do duplicate
     * validation and error toasts there). Every visible slot has its
     * own live input now, so there's no single "active" cell to gate
     * these on.
     */
    onChangeMdbId?: (rowIndex: number, colIndex: number, value: string) => void;
    onChangeEvaDtsId?: (rowIndex: number, colIndex: number, value: string) => void;
    /** Fired on Enter inside either input — parent grid moves focus to the next slot's MDB input, mirroring PlanogramTabular's focusNextMdb. */
    onEnterNextSlot?: (rowIndex: number, colIndex: number) => void;
    /** Lets the parent grid keep a ref to this cell's MDB input for onEnterNextSlot's focus-the-next-one behavior. */
    registerMdbInputRef?: (rowIndex: number, colIndex: number, el: HTMLInputElement | null) => void;

    productSuggestions?: TOption[];
    productsLoading?: boolean;
    onSearchProducts?: (event: ProductSearchEvent) => void;
    onClearProductSearch?: (
        rowIndex: number,
        colIndex: number,
        text: string,
    ) => void;

    getProductOptionLabel?: (option: TOption) => string;
    getProductOptionValue?: (option: TOption) => string;
    getProductAutoSelectMatchValues?: (
        option: TOption,
    ) => Array<string | number | null | undefined>;

    onDuplicateCell?: (rowIndex: number, colIndex: number) => void;
    onCancelCopyMode?: () => void;
    /**
     * Called when the copy-mode Save button is pressed on the source
     * cell. Separate from onCancelCopyMode (GH #544) — Save must clear
     * copy-mode state WITHOUT reverting the copies that were just made,
     * whereas Cancel reverts every copied cell back to what it held
     * before the session started.
     */
    onConfirmCopyMode?: () => void;
    isCopyModeActive?: boolean;
    isCopySourceCell?: boolean;
    copiedCellsCount?: number;
    onEmptyProductAction?: (query: string) => void;
    /**
     * Fired whenever any focusable element inside this cell (product
     * search, price/min/max inputs, or now the MDB/EVA-DTS slot inputs)
     * receives focus — including focus that arrives via Tab/Shift+Tab,
     * not just a mouse click. See GH #532.
     */
    onCellFocus?: (rowIndex: number, colIndex: number) => void;

    /**
     * "Blank layout" edit state: the planogram hasn't been initialized
     * yet and the person just pressed "Edit" on PlanogramDevice to
     * define rows/columns/merges — before any MDB/EVA-DTS assignment or
     * product data exists (see PlanogramDevice's
     * `isUninitializedSlotLayoutEdit`). There is nothing for the
     * product-entry card (Name/Price/Min/Max) or the Copy/Clear toolbar
     * buttons to act on yet, so this cell renders as a plain empty slot
     * with only the "more options" trigger left. Defaults to `false` so
     * every other caller/mode is completely unaffected.
     */
    hideCellMenu?: boolean;

    /**
     * True when this cell's mdbId and/or evaDtsId duplicates another
     * slot's value elsewhere in the planogram (computed by the parent —
     * see PlanogramVisual's buildDuplicateKeySet/isCellIdInvalid, which
     * mirrors PlanogramTabular's identical local computation). Drives
     * the green ("valid, filled") vs red ("duplicate, needs fixing")
     * background on the initialization slot card during ACTIVE manual
     * editing only — see renderInitializationSlotCard below. Has no
     * effect on the passive/view-only badge (see renderCell), which is
     * deliberately colorless per the Figma reference — coloring only
     * ever appears while a slot is actively being assigned.
     */
    isInvalid?: boolean;

    /**
     * ADD_PRODUCT "preview": the planogram already has products
     * assigned and the person hasn't pressed "Edit" (or clicked a
     * badge, which does the same thing) yet on PlanogramDevice — see
     * that component's `isAddProductPreview`. Distinct from
     * `isReadOnlyMode` (which means genuinely locked, e.g. another user
     * holds the lock, or INITIALIZATION mode isn't actively being
     * edited): a preview cell still shows its "more options" (⋮)
     * trigger and is still clickable to enter edit mode, whereas a
     * truly read-only cell hides the trigger entirely. See renderCell
     * below for how the two combine.
     */
    isAddProductPreview?: boolean;
}

const PlanogramCell = <TOption extends ProductOption = ProductOption>({
    cell,
    rowIndex,
    colIndex,
    onEditProduct,
    onMergeRight,
    onMergeLeft,
    onMergeMultiple,
    onClearCell,
    onAddProduct,
    onUpdateCellField,
    onAssignInitialization,
    onEnterAddProductEdit,
    onTestCell,
    isSelectionMode = false,
    isSelected = false,
    onSelectionToggle,
    totalColumns,
    onUnmerge,
    isRowFullyMerged = false,
    mode = PlanogramEditMode.ADD_PRODUCT as PlanogramEditModeValue,
    onlyMerge = false,
    isReadOnlyMode = false,
    supportEveDts = false,
    manualInitializationActive = false,
    onChangeMdbId,
    onChangeEvaDtsId,
    onEnterNextSlot,
    registerMdbInputRef,

    productSuggestions = [],
    productsLoading = false,
    onSearchProducts,
    onClearProductSearch,
    getProductOptionLabel = option => option?.label ?? option?.name ?? '',
    getProductOptionValue = option =>
        String(option?.value ?? option?.id ?? option?.externalId ?? ''),
    getProductAutoSelectMatchValues = option => [
        option?.label,
        option?.name,
        option?.ean,
        option?.systemId,
        option?.externalId,
    ],
    onDuplicateCell,
    onCancelCopyMode,
    onConfirmCopyMode,
    isCopyModeActive = false,
    isCopySourceCell = false,
    copiedCellsCount = 0,
    onEmptyProductAction,
    onCellFocus,
    hideCellMenu = false,
    isInvalid = false,
    isAddProductPreview = false,
}: PlanogramCellProps<TOption>) => {
    const { t } = useTranslation();
    const [showContextMenu, setShowContextMenu] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState({
        x: 0,
        y: 0,
    });

    // *** JS-state tooltip (replaces both the earlier PrimeReact <Tooltip>
    // and the CSS-only ::after attempt) ***
    //
    // Rendering the tooltip as `position: fixed` (computed from the
    // hovered button's real on-screen rect) escapes `.grid-cell`'s
    // `overflow: hidden` ancestor, and since it's plain React state tied
    // to this single cell instance, there's no separate component
    // lifecycle that can desync from the actual mouse position.
    const [hoveredTooltip, setHoveredTooltip] = useState<{
        label: string;
        top: number;
        left: number;
    } | null>(null);

    const showButtonTooltip = (
        event: React.MouseEvent<HTMLButtonElement>,
        label: string,
    ) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setHoveredTooltip({
            label,
            top: rect.top,
            left: rect.left + rect.width / 2,
        });
    };

    const hideButtonTooltip = () => setHoveredTooltip(null);

    // Tab/Shift+Tab keyboard navigation (GH #532): any of the four
    // per-cell fields (product search, price, min, max — and now the
    // MDB/EVA-DTS slot inputs) receiving focus — whether by click or by
    // tabbing in from a neighboring cell — marks this cell as the
    // active one.
    const handleFieldFocus = () => {
        onCellFocus?.(rowIndex, colIndex);
    };

    useEffect(() => {
        if (isSelectionMode || isCopyModeActive) {
            setShowContextMenu(false);
            hideButtonTooltip();
        }
    }, [isSelectionMode, isCopyModeActive]);

    const getParsedValue = (value: unknown): string => {
        if (value === null || value === undefined) return '';

        if (typeof value === 'object') {
            const parsed = value as { parsedValue?: unknown; source?: unknown };
            return String(parsed.parsedValue ?? parsed.source ?? '');
        }

        return String(value);
    };

    // -----------------------------------------------------------------
    // BADGE FIX: the passive assigned-slot badge and the product-preview
    // card's MDB badge used to render `targetCell.mdbId` raw. Some API
    // payloads carry mdbId in the same `{ parsedValue, source }` wrapped
    // shape that price/minQuantity/maxQuantity already go through
    // getParsedValue for — rendering it raw meant those cells showed an
    // empty badge (a purple box with nothing inside) even though a value
    // technically existed. This also falls back to `position` when
    // mdbId itself is genuinely absent, since some payloads carry the
    // slot's display number there instead.
    // -----------------------------------------------------------------
    const getMdbIdDisplay = (targetCell: PlanogramCellData): string => {
        const parsedMdb = getParsedValue(targetCell?.mdbId);
        if (parsedMdb) return parsedMdb;

        const parsedPosition = getParsedValue((targetCell as Record<string, unknown>)?.position);
        return parsedPosition;
    };


    const handleMoreOptionsClick = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (isSelectionMode || isCopyModeActive) {
            setShowContextMenu(false);
            hideButtonTooltip();
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();

        setContextMenuPosition({
            x: rect.left,
            y: rect.bottom + 5,
        });

        setShowContextMenu(true);
    };

    const handleContextMenuClose = () => {
        setShowContextMenu(false);
    };

    // *** FIX: the context menu wasn't reliably closing on outside
    // clicks — a document-level capture-phase listener so it always
    // sees the click/mousedown before anything else does.
    useEffect(() => {
        if (!showContextMenu) return;

        const handleOutsideInteraction = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (target?.closest('.context-menu')) return;
            setShowContextMenu(false);
        };

        document.addEventListener('mousedown', handleOutsideInteraction, true);
        return () => document.removeEventListener('mousedown', handleOutsideInteraction, true);
    }, [showContextMenu]);

    const getProductName = (targetCell: PlanogramCellData): string =>
        targetCell?.product?.name ||
        targetCell?.product?.label ||
        targetCell?.productName ||
        '';

    const getPrice = (targetCell: PlanogramCellData): string =>
        getParsedValue(targetCell?.price) ||
        getParsedValue(targetCell?.product?.price) ||
        '';

    const getMinQuantity = (targetCell: PlanogramCellData): string =>
        getParsedValue(targetCell?.minQuantity) ||
        getParsedValue(targetCell?.product?.minQuantity) ||
        '0';

    const getMaxQuantity = (targetCell: PlanogramCellData): string =>
        getParsedValue(targetCell?.maxQuantity) ||
        getParsedValue(targetCell?.product?.maxQuantity) ||
        '0';

    const getDisplayText = (value: unknown): string => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string' || typeof value === 'number') return String(value);

        if (typeof value === 'object') {
            const record = value as Record<string, unknown>;
            const candidate =
                record.name ??
                record.label ??
                record.parsedValue ??
                record.source ??
                record.en ??
                record.pl ??
                record.value;

            return getDisplayText(candidate);
        }

        return '';
    };

    const getProductCategoryName = (targetCell: PlanogramCellData): string => {
        const cellRecord = targetCell as Record<string, unknown>;
        const productRecord = (targetCell.product ?? {}) as Record<string, unknown>;

        return (
            getDisplayText(cellRecord.categoryName) ||
            getDisplayText(productRecord.categoryName) ||
            getDisplayText(cellRecord.category) ||
            getDisplayText(productRecord.category) ||
            ''
        );
    };

    const getProductCategoryColor = (targetCell: PlanogramCellData): string => {
        const cellRecord = targetCell as Record<string, unknown>;
        const productRecord = (targetCell.product ?? {}) as Record<string, unknown>;
        const categoryRecord = (productRecord.category ?? cellRecord.category ?? {}) as Record<string, unknown>;

        return (
            getDisplayText(cellRecord.categoryColor) ||
            getDisplayText(productRecord.categoryColor) ||
            getDisplayText(categoryRecord.color) ||
            getDisplayText(categoryRecord.backgroundColor) ||
            '#0ab38e'
        );
    };

    const getProductEan = (targetCell: PlanogramCellData): string | number | null => {
        const cellRecord = targetCell as Record<string, unknown>;
        const productRecord = (targetCell.product ?? {}) as Record<string, unknown>;
        const ean = productRecord.ean ?? cellRecord.ean;

        if (ean === null || ean === undefined || ean === '') return null;
        if (typeof ean === 'string' || typeof ean === 'number') return ean;

        return getDisplayText(ean) || null;
    };

    const getPriceDisplay = (targetCell: PlanogramCellData): string => {
        const price = getPrice(targetCell);
        const cellRecord = targetCell as Record<string, unknown>;
        const productRecord = (targetCell.product ?? {}) as Record<string, unknown>;
        const currency = getDisplayText(cellRecord.currency) || getDisplayText(productRecord.currency);

        if (!price) return '';
        if (!currency || price.toLowerCase().includes(currency.toLowerCase())) return price;

        return `${price} ${currency}`;
    };

    const handleProductSelect = (productOption: TOption) => {
        onAddProduct?.(rowIndex, colIndex, cell, {
            ...productOption,
            id: productOption?.externalId || productOption?.id || productOption?.value,
            name: productOption?.name || productOption?.label,
            label: productOption?.label || productOption?.name,
            price: getParsedValue(productOption?.price),
            minQuantity: getParsedValue(productOption?.minQuantity) || '0',
            maxQuantity: getParsedValue(productOption?.maxQuantity) || '0',
        } as TOption);
    };

    const handleFieldChange = (field: string, value: string) => {
        onUpdateCellField?.(rowIndex, colIndex, field, value);
    };

    const renderSelectionCheckbox = () => {
        if (!isSelectionMode) return null;

        return (
            <div
                className={`selection-checkbox ${isSelected ? 'selected' : ''}`}
                onMouseDown={event => {
                    event.stopPropagation();
                }}
                onClick={event => {
                    event.stopPropagation();
                    onSelectionToggle?.(rowIndex, colIndex);
                }}
            >
                {isSelected && <i className="pi pi-check" />}
            </div>
        );
    };

    const renderCellToolbar = () => {
        if (isReadOnlyMode) return null;

        // Selection mode: show only checkboxes, no regular cell actions.
        if (isSelectionMode) return null;

        // Copy mode: only the source cell shows copy-mode controls.
        if (isCopyModeActive && !isCopySourceCell) return null;

        if (hideCellMenu) {
            return (
                <div className="cell-toolbar">
                    <button
                        type="button"
                        className="cell-toolbar-btn more"
                        tabIndex={-1}
                        onMouseDown={event => event.stopPropagation()}
                        onClick={handleMoreOptionsClick}
                    >
                        <MoreIcon />
                    </button>
                </div>
            );
        }

        const stopMouseDownPropagation = (event: React.MouseEvent) => {
            event.stopPropagation();
        };

        const handleCancelCopyMode = (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            hideButtonTooltip();
            onCancelCopyMode?.();
        };

        return (
            <div className={`cell-toolbar ${isCopySourceCell ? 'copy-source-toolbar' : ''}`}>
                    {isCopySourceCell && (
                        <button
                            type="button"
                            className="cell-toolbar-btn copy-cancel"
                            aria-label={t('common.cancel', 'Cancel')}
                            tabIndex={-1}
                            onMouseDown={stopMouseDownPropagation}
                            onMouseEnter={event =>
                                showButtonTooltip(event, t('common.cancel', 'Cancel'))
                            }
                            onMouseLeave={hideButtonTooltip}
                            onClick={handleCancelCopyMode}
                        >
                            <CancelRedIcon />
                        </button>
                    )}

                    <button
                        type="button"
                        className={`cell-toolbar-btn copy ${
                        isCopySourceCell ? 'copy-source-active' : ''
                    }`}
                    tabIndex={-1}
                    onMouseDown={stopMouseDownPropagation}
                    onMouseEnter={event => showButtonTooltip(event, isCopySourceCell ? 'Save' : 'Copy cell')}
                    onMouseLeave={hideButtonTooltip}
                    onClick={event => {
                        event.stopPropagation();
                        hideButtonTooltip();

                        if (isCopySourceCell) {
                            onConfirmCopyMode?.();
                        } else {
                            onDuplicateCell?.(rowIndex, colIndex);
                        }
                    }}
                >
                    {isCopySourceCell ? <SaveIcon /> : <CopyIcon />}
                </button>

                {isCopySourceCell && copiedCellsCount > 0 && (
                    <span className="copy-counter">
                        {copiedCellsCount}
                    </span>
                )}

                {!isCopyModeActive && (
                    <button
                        type="button"
                        className="cell-toolbar-btn delete"
                        tabIndex={-1}
                        onMouseDown={stopMouseDownPropagation}
                        onMouseEnter={event => showButtonTooltip(event, 'Clear cell')}
                        onMouseLeave={hideButtonTooltip}
                        onClick={event => {
                            event.stopPropagation();
                            hideButtonTooltip();
                            onClearCell?.(rowIndex, colIndex);
                        }}
                    >
                        <TrashIcon />
                    </button>
                )}

                {!isSelectionMode && !isCopyModeActive && (
                    <button
                        type="button"
                        className="cell-toolbar-btn more"
                        tabIndex={-1}
                        onMouseDown={stopMouseDownPropagation}
                        onClick={handleMoreOptionsClick}
                    >
                        <MoreIcon />
                    </button>
                )}
            </div>
        );
    };

    const renderProductEditorCard = (targetCell: PlanogramCellData) => (
        <div className="planogram-cell-card">
            {/* MDB/EVA-DTS identifiers are part of initialization only.
                Do not render them in ADD_PRODUCT edit mode. */}

            <div className="cell-label">
                {t('planogram.cell.name', 'NAME')}
            </div>

            <ResourceAutoComplete<TOption>
                value={getProductName(targetCell)}
                suggestions={productSuggestions}
                loading={productsLoading}
                disabled={isReadOnlyMode || mode !== PlanogramEditMode.ADD_PRODUCT}
                placeholder={t('common.select', 'SELECT')}
                completeMethod={onSearchProducts ?? (() => undefined)}
                getOptionLabel={getProductOptionLabel}
                getOptionValue={getProductOptionValue}
                getAutoSelectMatchValues={getProductAutoSelectMatchValues}
                onSelect={handleProductSelect}
                onClear={text => {
                    onClearProductSearch?.(rowIndex, colIndex, text);
                }}
                className="cell-product-autocomplete"
                emptyActionLabel={t('planogram.cellSettings.newProduct', 'New product')}
                onEmptyAction={onEmptyProductAction}
                tabIndex={isReadOnlyMode ? -1 : 0}
                onFocus={handleFieldFocus}
            />

            <div className="cell-label">
                {t('planogram.cell.price', 'PRICE')}
            </div>

            <input
                type="text"
                inputMode="decimal"
                className="cell-inline-input cell-price-input"
                value={getPrice(targetCell)}
                disabled={isReadOnlyMode}
                placeholder={t('planogram.cell.pricePlaceholder', 'PRICE')}
                onChange={event => handleFieldChange('price', parsePriceFieldInput(event.target.value))}
                onFocus={handleFieldFocus}
            />

            <div className="cell-minmax-labels">
                <span>{t('planogram.cell.min', 'MIN')}</span>
                <span>{t('planogram.cell.max', 'MAX')}</span>
            </div>

            <div className="cell-minmax-inputs">
                <input
                    type="number"
                    className="cell-inline-input"
                    value={getMinQuantity(targetCell)}
                    disabled={isReadOnlyMode}
                    placeholder={t('planogram.cell.quantityPlaceholder', 'TL...')}
                    onChange={event => handleFieldChange('minQuantity', event.target.value)}
                    onFocus={handleFieldFocus}
                />

                <input
                    type="number"
                    className="cell-inline-input"
                    value={getMaxQuantity(targetCell)}
                    disabled={isReadOnlyMode}
                    placeholder={t('planogram.cell.quantityPlaceholder', 'TL...')}
                    onChange={event => handleFieldChange('maxQuantity', event.target.value)}
                    onFocus={handleFieldFocus}
                />
            </div>
        </div>
    );

    const renderCellHeader = () => (
        <div className="cell-header">
            <div className="cell-header-left">
                {renderSelectionCheckbox()}
            </div>

            <div className="cell-header-right">
                {renderCellToolbar()}
            </div>
        </div>
    );

    const renderEditableCell = (cellClasses: string) => (
        <div className={cellClasses}>
            {renderCellHeader()}
            {!hideCellMenu && renderProductEditorCard(cell)}
        </div>
    );

    // -----------------------------------------------------------------
    // Always-visible per-slot MDB / EVA-DTS ID card, matching the Figma
    // reference exactly: the "more" (⋮) trigger is `visibility: hidden`
    // in Figma's own spec for these cards — no header/toolbar/selection
    // checkbox is shown on an initialization slot at all, filled or
    // empty. renderCellHeader() is therefore deliberately NOT called
    // here (unlike renderEditableCell above, which still needs it for
    // ADD_PRODUCT mode's Copy/Clear/More actions).
    //
    // Color follows the same rule PlanogramTabular already uses for its
    // MDB/EVA-DTS input cells: untouched empty slot -> neutral/default background;
    // assigned slot -> green if the value is unique across the planogram;
    // missing required MDB or duplicate/external invalid slot -> red
    // (isInvalid, computed by the
    // parent grid — see PlanogramVisual's buildDuplicateKeySet). This
    // coloring is exclusive to ACTIVE manual editing — see the plain
    // passive badge branch in renderCell below for the view-only state.
    // -----------------------------------------------------------------
    const renderInitializationSlotCard = (targetCell: PlanogramCellData, cellClasses: string) => {
        const mdbValue = targetCell?.mdbId === null || targetCell?.mdbId === undefined ? '' : String(targetCell.mdbId);
        const evaValue = targetCell?.evaDtsId === null || targetCell?.evaDtsId === undefined ? '' : String(targetCell.evaDtsId);
        const hasMdb = mdbValue.trim().length > 0;

        const validityClass = isInvalid
            ? 'initialization-cell--invalid'
            : (hasMdb ? 'initialization-cell--valid' : '');

        const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
            // useGridEnterNavigation listens in the capture phase and now owns
            // Enter / Shift+Enter during initialization. If it already handled
            // this keypress, do not run the legacy "jump to next MDB" fallback.
            if (event.defaultPrevented) return;

            if (event.key === 'Enter') {
                event.preventDefault();
                onEnterNextSlot?.(rowIndex, colIndex);
            }
        };

        return (
            <div className={`${cellClasses} initialization-cell ${validityClass}`}>
                <div className="initialization-slot-card">
                    <div className="slot-field-label">
                        {t('planogram.device.assignId.enterIdMdb', 'ENTER ID MDB')}
                    </div>
                    <div className="slot-field-hint">
                        {t(
                            'planogram.device.assignId.mdbHint',
                            'Identifier assigned to this product slot',
                        )}
                    </div>

                    <div className="slot-input-frame">
                        <input
                            id={`initialization-mdb-input-${rowIndex}-${colIndex}`}
                            type="text"
                            className="slot-input slot-input--mdb"
                            value={mdbValue}
                            placeholder={t('planogram.device.assignId.mdbIdPlaceholder', 'MDB ID')}
                            disabled={isReadOnlyMode}
                            onChange={event => onChangeMdbId?.(rowIndex, colIndex, event.target.value)}
                            onKeyDown={handleKeyDown}
                            onFocus={handleFieldFocus}
                            ref={el => registerMdbInputRef?.(rowIndex, colIndex, el)}
                        />
                    </div>

                    {supportEveDts && (
                        <div className="slot-input-frame slot-input-frame--eva">
                            <input
                                type="text"
                                className="slot-input slot-input--eva"
                                value={evaValue}
                                placeholder={t('planogram.device.assignId.evaDtsId', 'EVA DTS ID')}
                                disabled={isReadOnlyMode}
                                onChange={event => onChangeEvaDtsId?.(rowIndex, colIndex, event.target.value)}
                                onKeyDown={handleKeyDown}
                                onFocus={handleFieldFocus}
                            />
                        </div>
                    )}

                    <div className="slot-field-footer">
                        {t(
                            'planogram.device.assignId.saveInstructionPlain',
                            'Press Enter to move to the next slot',
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // -----------------------------------------------------------------
    // Passive / view-only assigned slot badge. Shown in TWO distinct
    // preview situations, both driven purely by `isReadOnlyMode`/
    // `isAddProductPreview` + `mode`:
    //
    //   1. INITIALIZATION mode, manual editing not currently active —
    //      the planogram has already been (re)initialized and is just
    //      being viewed. Clicking re-enters manual MDB assignment via
    //      onAssignInitialization.
    //   2. ADD_PRODUCT mode, "preview" (isAddProductPreview === true,
    //      or isReadOnlyMode === true for a genuinely locked planogram)
    //      — the planogram already has products assigned and is just
    //      being viewed, matching the Figma "preview mode" reference
    //      (Re-Initialization / Assign planogram / Edit buttons, plain
    //      numbered badges). Clicking instead calls
    //      onEnterAddProductEdit, which flips PlanogramDevice into the
    //      full NAME/PRICE/MIN/MAX editor view.
    //
    // Matches the Figma reference exactly either way: plain lavender
    // .empty-cell background (no green/red fill — that's reserved for
    // active MDB editing, see renderInitializationSlotCard above), a
    // small purple numbered badge pinned to the bottom-right corner,
    // and an always-visible "more" trigger pinned to the top-right
    // corner (opening the same merge/Test-Cell context menu used
    // elsewhere) — the trigger only hides when the planogram is
    // genuinely locked (isReadOnlyMode), not during a plain preview.
    // -----------------------------------------------------------------
    const renderProductPreviewCard = (targetCell: PlanogramCellData, cellClasses: string) => {
        const productName = getProductName(targetCell) || t('planogram.cell.productName', 'Product Name');
        const categoryName = getProductCategoryName(targetCell);
        const categoryColor = getProductCategoryColor(targetCell);
        const priceDisplay = getPriceDisplay(targetCell);
        const productEan = getProductEan(targetCell);
        const mdbDisplay = getMdbIdDisplay(targetCell);

        const handleClick = () => {
            if (isReadOnlyMode || isSelectionMode) return;
            onEnterAddProductEdit?.();
        };

        return (
            <div className={`${cellClasses} product-preview-cell`} onClick={handleClick}>
                {renderSelectionCheckbox()}

                {!isReadOnlyMode && !isSelectionMode && (
                    <button
                        type="button"
                        className="cell-toolbar-btn more standalone-more product-preview-more"
                        tabIndex={-1}
                        onMouseDown={event => event.stopPropagation()}
                        onClick={handleMoreOptionsClick}
                    >
                        <MoreIcon />
                    </button>
                )}

                <div className="product-preview-card">
                    <div
                        className="product-preview-category"
                        style={{ backgroundColor: categoryColor }}
                    >
                        {categoryName}
                    </div>

                    <div className="product-preview-image-container">
                        <PlanogramCellProductPhoto
                            ean={productEan}
                            alt={productName}
                        />
                    </div>

                    <div className="product-preview-text">
                        <div className="product-preview-name" title={productName}>
                            {productName}
                        </div>
                        <div className="product-preview-price">
                            {priceDisplay || '-'}
                        </div>
                    </div>

                    {mdbDisplay && (
                        <div className="mdb-id-badge product-preview-mdb-badge">
                            {mdbDisplay}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderAssignedSlotBadge = (targetCell: PlanogramCellData, cellClasses: string) => {
        const isAddProductMode = mode === PlanogramEditMode.ADD_PRODUCT;
        const mdbDisplay = getMdbIdDisplay(targetCell);

        const handleClick = () => {
            if (isReadOnlyMode) return;
            if (isAddProductMode) {
                onEnterAddProductEdit?.();
            } else {
                onAssignInitialization?.(rowIndex, colIndex, targetCell);
            }
        };

        return (
            <div className={cellClasses} onClick={handleClick}>
                {!isReadOnlyMode && (
                    <button
                        type="button"
                        className="cell-toolbar-btn more standalone-more"
                        tabIndex={-1}
                        onMouseDown={event => event.stopPropagation()}
                        onClick={handleMoreOptionsClick}
                    >
                        <MoreIcon />
                    </button>
                )}

                {/* Always render some content in the badge — falls back to
                    '-' rather than an empty box when neither mdbId nor
                    position is available, so the slot never looks broken. */}
                <div className="mdb-id-badge">
                    {mdbDisplay || '-'}
                </div>
            </div>
        );
    };

    const renderBlankSlotLayoutCell = (cellClasses: string) => {
        return (
            <div className={cellClasses}>
                {!isReadOnlyMode && (
                    <button
                        type="button"
                        className="cell-toolbar-btn more standalone-more"
                        tabIndex={-1}
                        onMouseDown={event => event.stopPropagation()}
                        onClick={handleMoreOptionsClick}
                    >
                        <MoreIcon />
                    </button>
                )}
            </div>
        );
    };

    const renderCell = (targetCell: PlanogramCellData) => {
        const cellClasses = `empty-cell ${isSelectionMode ? 'selection-mode' : ''} ${isSelected ? 'selected' : ''}`;

        const isInitializationMode = mode === PlanogramEditMode.INITIALIZATION;
        const isAddProductMode = mode === PlanogramEditMode.ADD_PRODUCT;
        const isEmptyType =
            targetCell.type === 'empty' ||
            targetCell.type === 'empty_merged';
        const isProductType =
            targetCell.type === 'product' &&
            !!targetCell.product;
        const isUninitializedSlotLayoutEdit =
            isInitializationMode &&
            hideCellMenu &&
            !manualInitializationActive;

        if (
            isUninitializedSlotLayoutEdit &&
            (isEmptyType || isProductType)
        ) {
            return renderBlankSlotLayoutCell(cellClasses);
        }

        if (
            isInitializationMode &&
            manualInitializationActive &&
            (isEmptyType || isProductType)
        ) {
            return renderInitializationSlotCard(targetCell, cellClasses);
        }

        if (
            isInitializationMode &&
            !manualInitializationActive &&
            (isEmptyType || isProductType)
        ) {
            return renderAssignedSlotBadge(targetCell, cellClasses);
        }

        if (isAddProductMode && (isEmptyType || isProductType)) {
            if (isReadOnlyMode || isAddProductPreview) {
                if (isProductType) {
                    return renderProductPreviewCard(targetCell, cellClasses);
                }

                return renderAssignedSlotBadge(targetCell, cellClasses);
            }

            return renderEditableCell(cellClasses);
        }

        return null;
    };

    const hasProduct =
        cell?.type === 'product' &&
        !!cell.product &&
        !!cell.product.id;

    return (
        <>
            {renderCell(cell)}

            {hoveredTooltip && (
                <div
                    className="cell-toolbar-js-tooltip"
                    style={
                        {
                            position: 'fixed',
                            top: hoveredTooltip.top,
                            left: hoveredTooltip.left,
                            transform: 'translate(-50%, -100%) translateY(-8px)',
                        } as CSSProperties
                    }
                >
                    {hoveredTooltip.label}
                </div>
            )}

           {showContextMenu && (
                <div
                    className="context-menu"
                    onMouseDown={event => {
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                    onClick={event => {
                        event.stopPropagation();
                    }}
                    style={
                        {
                            left: contextMenuPosition.x,
                            top: contextMenuPosition.y,
                        } as CSSProperties
                    }
                >
                    {mode === PlanogramEditMode.INITIALIZATION ? (
                        <>
                            {onlyMerge ? (
                                <>
                                    <div
                                        className={`context-menu-item ${colIndex === totalColumns - 1 || isRowFullyMerged ? 'disabled' : ''}`}
                                        onClick={() => {
                                            if (colIndex === totalColumns - 1 || isRowFullyMerged) return;
                                            onMergeRight?.(rowIndex, colIndex);
                                            handleContextMenuClose();
                                        }}
                                    >
                                        <MergeRightIcon />
                                        <span>{t('planogram.mergeWithRight')}</span>
                                    </div>

                                    <div
                                        className={`context-menu-item ${colIndex === 0 || isRowFullyMerged ? 'disabled' : ''}`}
                                        onClick={() => {
                                            if (colIndex === 0 || isRowFullyMerged) return;
                                            onMergeLeft?.(rowIndex, colIndex);
                                            handleContextMenuClose();
                                        }}
                                    >
                                        <MergeLeftIcon />
                                        <span>{t('planogram.mergeWithLeft')}</span>
                                    </div>

                                    <div
                                        className={`context-menu-item ${isRowFullyMerged || isSelectionMode ? 'disabled' : ''}`}
                                        onClick={() => {
                                            if (isRowFullyMerged || isSelectionMode) return;
                                            onMergeMultiple?.(rowIndex, colIndex);
                                            handleContextMenuClose();
                                        }}
                                    >
                                        <MergeMultipleIcon />
                                        <span>{t('planogram.mergeMultiple')}</span>
                                    </div>

                                    <div
                                        className={`context-menu-item unmerge-item ${!(cell?.merged && cell?.mergeData?.colspan && cell.mergeData.colspan > 1) ? 'disabled' : ''}`}
                                        onClick={() => {
                                            if (!(cell?.merged && cell?.mergeData?.colspan && cell.mergeData.colspan > 1)) return;
                                            onUnmerge?.(rowIndex, colIndex);
                                            handleContextMenuClose();
                                        }}
                                    >
                                        <UngroupIcon />
                                        <span>{t('planogram.ungroupCell')}</span>
                                    </div>
                                </>
                            ) : (
                                <div
                                    className="context-menu-item"
                                    onClick={() => {
                                        onTestCell?.(rowIndex, colIndex, cell);
                                        handleContextMenuClose();
                                    }}
                                >
                                    <RefreshIcon />
                                    <span>{t('planogram.testCell')}</span>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div
                                className={`context-menu-item ${!hasProduct ? 'disabled' : ''}`}
                                onClick={() => {
                                    if (!hasProduct) return;
                                    onEditProduct?.(rowIndex, colIndex);
                                    handleContextMenuClose();
                                }}
                            >
                                <EditIcon />
                                <span>{t('planogram.edit')}</span>
                            </div>

                            <div className="context-menu-separator" />

                            <div
                                className={`context-menu-item ${colIndex === totalColumns - 1 || isRowFullyMerged ? 'disabled' : ''}`}
                                onClick={() => {
                                    if (colIndex === totalColumns - 1 || isRowFullyMerged) return;
                                    onMergeRight?.(rowIndex, colIndex);
                                    handleContextMenuClose();
                                }}
                            >
                                <MergeRightIcon />
                                <span>{t('planogram.mergeWithRight')}</span>
                            </div>

                            <div
                                className={`context-menu-item ${colIndex === 0 || isRowFullyMerged ? 'disabled' : ''}`}
                                onClick={() => {
                                    if (colIndex === 0 || isRowFullyMerged) return;
                                    onMergeLeft?.(rowIndex, colIndex);
                                    handleContextMenuClose();
                                }}
                            >
                                <MergeLeftIcon />
                                <span>{t('planogram.mergeWithLeft')}</span>
                            </div>

                            <div
                                className={`context-menu-item ${isRowFullyMerged || isSelectionMode ? 'disabled' : ''}`}
                                onClick={() => {
                                    if (isRowFullyMerged || isSelectionMode) return;
                                    onMergeMultiple?.(rowIndex, colIndex);
                                    handleContextMenuClose();
                                }}
                            >
                                <MergeMultipleIcon />
                                <span>{t('planogram.mergeMultiple')}</span>
                            </div>

                            <div className="context-menu-separator" />

                            <div
                                className={`context-menu-item unmerge-item ${!(cell?.merged && cell?.mergeData?.colspan && cell.mergeData.colspan > 1) ? 'disabled' : ''}`}
                                onClick={() => {
                                    if (!(cell?.merged && cell?.mergeData?.colspan && cell.mergeData.colspan > 1)) return;
                                    onUnmerge?.(rowIndex, colIndex);
                                    handleContextMenuClose();
                                }}
                            >
                                <UngroupIcon />
                                <span>{t('planogram.ungroupCell')}</span>
                            </div>

                            <div className="context-menu-separator" />

                            <div
                                className={`context-menu-item delete ${!hasProduct ? 'disabled' : ''}`}
                                onClick={() => {
                                    if (!hasProduct) return;
                                    onClearCell?.(rowIndex, colIndex);
                                    handleContextMenuClose();
                                }}
                            >
                                <TrashIcon />
                                <span>{t('planogram.clearCell')}</span>
                            </div>
                        </>
                    )}
                </div>
            )}

            {showContextMenu && (
                <div
                    className="context-menu-overlay"
                    onClick={handleContextMenuClose}
                />
            )}
        </>
    );
};

export default PlanogramCell;